const KLAVIYO_CLIENT_API = "https://a.klaviyo.com/client";
const KLAVIYO_REVISION = "2026-07-15";

class NotifyMeModal extends HTMLElement {
  connectedCallback() {
    this.companyId = this.dataset.companyId;
    this.listId = this.dataset.listId;
    this.variantId = this.dataset.variantId;

    this.dialog = this.querySelector("[data-notify-me-dialog]");
    this.overlay = this.querySelector("[data-notify-me-overlay]");
    this.openBtn = this.querySelector("[data-notify-me-open]");
    this.closeBtn = this.querySelector("[data-notify-me-close]");
    this.form = this.querySelector("[data-notify-me-form]");
    this.formState = this.querySelector("[data-notify-me-form-state]");
    this.successState = this.querySelector("[data-notify-me-success-state]");
    this.error = this.querySelector("[data-notify-me-error]");

    this.handleOpen = this.open.bind(this);
    this.handleClose = this.close.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleSubmit = this.onSubmit.bind(this);

    this.openBtn.addEventListener("click", this.handleOpen);
    this.closeBtn.addEventListener("click", this.handleClose);
    this.overlay.addEventListener("click", this.handleClose);
    this.form.addEventListener("submit", this.handleSubmit);

    // Escape ancestor transforms (scroll-reveal) that break fixed positioning.
    document.body.appendChild(this.dialog);
  }

  disconnectedCallback() {
    this.dialog.remove();
    this.openBtn.removeEventListener("click", this.handleOpen);
    this.closeBtn.removeEventListener("click", this.handleClose);
    this.overlay.removeEventListener("click", this.handleClose);
    this.form.removeEventListener("submit", this.handleSubmit);
    document.removeEventListener("keydown", this.handleKeydown);
  }

  open() {
    this.dialog.classList.remove("hidden");
    this.dialog.classList.add("flex");
    document.addEventListener("keydown", this.handleKeydown);
    document.body.style.overflow = "hidden";
    const email = this.form.querySelector('input[type="email"]');
    if (email) email.focus();
  }

  close() {
    this.dialog.classList.add("hidden");
    this.dialog.classList.remove("flex");
    document.removeEventListener("keydown", this.handleKeydown);
    document.body.style.overflow = "";
    this.openBtn.focus();
  }

  onKeydown(event) {
    if (event.key === "Escape") this.close();
  }

  async onSubmit(event) {
    event.preventDefault();
    this.error.classList.add("hidden");

    const email = this.form.querySelector('input[name="email"]').value.trim();
    if (!email || !this.form.querySelector('input[name="email"]').checkValidity()) {
      this.error.classList.remove("hidden");
      return;
    }
    const optedIn = this.form.querySelector('input[name="marketing_optin"]').checked;

    const submitBtn = this.form.querySelector('button[type="submit"]');
    submitBtn.disabled = true;

    try {
      await this.createBackInStockSubscription(email);
      if (optedIn && this.listId) {
        await this.subscribeToMarketing(email);
      }
      this.formState.classList.add("hidden");
      this.successState.classList.remove("hidden");
      this.successState.focus();
    } catch (e) {
      this.error.classList.remove("hidden");
    } finally {
      submitBtn.disabled = false;
    }
  }

  async createBackInStockSubscription(email) {
    const res = await fetch(
      `${KLAVIYO_CLIENT_API}/back-in-stock-subscriptions/?company_id=${this.companyId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.api+json",
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: "back-in-stock-subscription",
            attributes: {
              channels: ["EMAIL"],
              profile: {
                data: { type: "profile", attributes: { email } },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: "catalog-variant",
                  id: `$shopify:::$default:::${this.variantId}`,
                },
              },
            },
          },
        }),
      }
    );
    if (!res.ok) throw new Error(`Back in stock subscription failed: ${res.status}`);
  }

  async subscribeToMarketing(email) {
    const res = await fetch(
      `${KLAVIYO_CLIENT_API}/subscriptions/?company_id=${this.companyId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.api+json",
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: "Back in stock modal",
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email,
                    subscriptions: {
                      email: { marketing: { consent: "SUBSCRIBED" } },
                    },
                  },
                },
              },
            },
            relationships: {
              list: { data: { type: "list", id: this.listId } },
            },
          },
        }),
      }
    );
    if (!res.ok) throw new Error(`Marketing subscription failed: ${res.status}`);
  }
}

if (!customElements.get("notify-me-modal")) {
  customElements.define("notify-me-modal", NotifyMeModal);
}
